import * as d3 from "d3";
import { countBy, find, first } from "underscore";
import { DimensionValues } from "../domain/dimension";
import { memoize } from "../lib/memoize";
import { newRequestAnimationFrameQueue } from "../lib/requestAnimationFrame";
import * as utils from "../lib/VisUtils";

const requestAnimationFrame = newRequestAnimationFrameQueue();

export default () => {
  let height = 0;
  let width = 0;
  let margin = { top: 0, right: 0, bottom: 0, left: 0 };
  let axisLabels = { min: "", max: "" };
  let useOutline = false;
  let selectedDimKey: any = null;
  let xdomain: any = null;
  let ydomain: any = null;
  let valueFormat = d3.format(".2f");
  // let duration = 200
  let showHistogram = false;
  const showAvgValueDot = true;
  let showAvgValueText = false;
  let showAxisTicks = false;
  let showKdePoints = false;
  let showKdePointLabels = false;
  let histogramBins: any = null;
  let kdeLineInterpolation = "monotone";
  let dimensionsOrdering: any = null;
  const dispatch: any = d3.dispatch("hover");

  const chart: any = (selection: any) => selection.each(render);

  const render = function (this: any, dimensionValues: DimensionValues[]) {
    const w = width - margin.left - margin.right;
    const h = height - margin.top - margin.bottom;
    const el = d3.select(this);

    // defs
    // --------------------------------------------------------------------------

    let defs = el.select("defs");
    if (defs.empty()) {
      defs = el.append("defs");
      defs
        .append("filter")
        .attr("class", "multiply-filter")
        .attr("id", utils.randomNodeId())
        .append("feBlend")
        .attr("mode", "multiply");
    }

    // container
    // --------------------------------------------------------------------------

    let container = el.select(".violin__chart");
    if (container.empty()) {
      container = el.append("g").attr("class", "violin__chart");
      container.append("line").attr("class", "violin__axis-line");
      container.append("g").attr("class", "violin__paths");
      container.append("g").attr("class", "violin__avgs");
      const axis = container.append("g").attr("class", "violin__axis");
      container.append("rect").attr("class", "violin__foreground").attr("fill-opacity", 0);
      axis.append("text").attr("class", "violin__axis-label is-min").attr("text-anchor", "end");
      axis.append("text").attr("class", "violin__axis-label is-max").attr("text-anchor", "start");
    }

    const mainDim = find(dimensionValues, (d) => d.key === selectedDimKey) || first(dimensionValues);
    if (!mainDim) {
      console.error("ViolinChart: mainDim");
      return;
    }

    container
      .select(".violin__axis")
      .attr("class", "violin__axis dim--" + mainDim.key)
      .attr("transform", "translate(0," + h / 2 + ")");

    container
      .select(".violin__axis-line")
      .attr("x1", 0)
      .attr("x2", w)
      .attr("y1", h / 2)
      .attr("y2", h / 2);

    container
      .select(".violin__axis-label.is-min")
      .attr("x", -7)
      .attr("y", h / 2 + 4)
      .text(axisLabels.min);

    container
      .select(".violin__axis-label.is-max")
      .attr("x", w + 7)
      .attr("y", h / 2 + 4)
      .text(axisLabels.max);

    const foreground = container.select(".violin__foreground").attr("width", w).attr("height", h);

    container.attr("transform", "translate(" + margin.left + "," + margin.top + ")");

    // kdeData
    // --------------------------------------------------------------------------

    // dimensionValues.forEach((d: any) => {
    //   d.kdeData = kde(d.values)
    // })

    // scales
    // --------------------------------------------------------------------------

    const [xmin, xmax] = xdomain != null ? xdomain : [0, d3.max(dimensionValues, (d) => d3.max(d.values)) + 1];
    const x = d3.scale.linear().domain([xmin, xmax]).range([0, w]);

    const [ymin, ymax] =
      ydomain != null ? ydomain : [0, d3.max(dimensionValues, (d: any) => d3.max(d.kdeData.map((dd: any) => dd[1])))];
    const y = d3.scale
      .linear()
      .range([h / 2, 0])
      .domain([ymin, ymax]);

    // axis
    // --------------------------------------------------------------------------

    const axisNode = container.select(".violin__axis");
    if (showAxisTicks) {
      axisNode.call(d3.svg.axis().orient("bottom").scale(x).tickValues([1, 2, 3, 4, 5, 6]).tickSize(5));
    } else {
      axisNode.selectAll(".tick").remove();
    }

    // paths
    // --------------------------------------------------------------------------

    const line = d3.svg
      .line()
      .x((d) => x(d[0]))
      .y((d) => y(d[1]))
      .interpolate(kdeLineInterpolation);

    const violinPaths = container.select(".violin__paths");

    const violin = violinPaths.selectAll(".violin__path").data(dimensionValues, (d) => d.key);

    violin.exit().classed("is-added", false);

    violin
      .enter()
      .append("path")
      .attr("class", (d) => "violin__path" + " " + "dim--" + d.key)
      .classed("is-outline-dim", useOutline)
      .attr("filter", (d) => {
        if (d.key !== mainDim.key) {
          return "url(#" + defs.select(".multiply-filter").attr("id") + ")";
        } else {
          return "";
        }
      });

    if (dimensionsOrdering != null) {
      container
        .selectAll(".violin__path")
        .sort((a: any, b: any) => d3.ascending(dimensionsOrdering[a.key], dimensionsOrdering[b.key]));
    }

    violin.attr("d", (d: any) => {
      const reverseData = d.kdeData.map((xy: any) => [xy[0], -xy[1]]).reverse();
      return line(d.kdeData.concat(reverseData));
    });

    requestAnimationFrame(() => {
      violin.classed("is-added", true);
    });

    // avgs
    // --------------------------------------------------------------------------

    const violinAvgs = container.select(".violin__avgs");
    const avg = violinAvgs.selectAll(".violin__avg").data(dimensionValues, (d) => d.key);
    avg.exit().classed("is-removed", true);
    const avgEnter = avg.enter().append("g");
    avg
      .attr("class", (d) => "violin__avg dim--" + d.key)
      .classed("is-avg-dim", (d) => d.key === mainDim.key)
      .attr("transform", (d) => "translate(" + x(d.avg) + ", " + y(0) + ")");

    if (showAvgValueDot) {
      avgEnter.append("circle").attr("class", "violin__avg-dot").attr("r", 4);
    }

    if (showAvgValueText) {
      let avgValueText: d3.Selection<DimensionValues> = avg.select(".violin__avg-value");
      if (avgValueText.empty()) {
        avgValueText = avg.append("text").attr("class", "violin__avg-value").attr("y", -10);
      }
      avgValueText.text((d) => valueFormat(d.avg));
    } else {
      avg.select(".violin__avg-value").text("");
    }

    const closest = (xv: number): number[] => {
      const kdeData = mainDim.kdeData;
      let minV = +2e308;
      let min;

      for (let i = 0, len = kdeData.length; i < len; ++i) {
        const v = Math.abs(kdeData[i][0] - xv);
        if (v < minV) {
          minV = v;
          min = kdeData[i];
        }
      }
      return min === undefined ? [0, 0] : min;
    };
    const kdePointFreqs = memoize((): any => countBy(mainDim.values, (v: any) => closest(v)[0]));

    // mouse
    // --------------------------------------------------------------------------

    foreground
      .on("mousemove", function (this: any) {
        const p = d3.mouse(this);
        const [xv, yv] = closest(x.invert(p[0]));
        const ref3 = kdePointFreqs()[xv];
        const freq = ref3 != null ? ref3 : 0;
        dispatch.hover({
          position: [x(xv), y(yv)],
          x: xv,
          value: freq,
          dimKey: mainDim.key,
        });
      })
      .on("mouseout", () => {
        dispatch.hover(null);
      });

    // debug
    // --------------------------------------------------------------------------

    if (showHistogram) {
      const bins = typeof histogramBins === "function" ? histogramBins(mainDim) : void 0;
      const histogramLayout = d3.layout.histogram().frequency(false).bins(bins);
      const histogramData = histogramLayout(mainDim.values);
      const binWidth = histogramData[0].dx;
      histogramData.forEach((d: any) => {
        d.probability = d.y;
        d.density = d.y / binWidth;
        d.frequency = Math.round(d.y * mainDim.values.length);
        d.valueToRepresent = d.probability;
        d.valueToShowInLabel = d.frequency;
      });
      const histogramBarWidth = x(binWidth) - 1;
      let histogram = container.select(".violin__histogram");
      if (histogram.empty()) {
        histogram = container.insert("g", ".violin__foreground").attr("class", "violin__histogram dim--" + mainDim.key);
      }
      const histogramBar = histogram.selectAll(".violin__histogram--bar").data(histogramData);
      const histogramBarEnter = histogramBar.enter().append("g").attr("class", "violin__histogram--bar");
      histogramBar.attr("transform", (d: any) => {
        return "translate(" + x(d.x) + ", " + y(d.valueToRepresent) + ")";
      });
      histogramBar.exit().remove();
      histogramBarEnter.append("rect").attr("class", "violin__histogram--bar-rect");
      histogramBarEnter
        .append("text")
        .attr("class", "violin__histogram--bar-text")
        .attr("dy", "-0.5em")
        .attr("text-anchor", "middle");
      histogramBar
        .select(".violin__histogram--bar-rect")
        .attr("x", 0)
        .attr("width", histogramBarWidth)
        .attr("height", (d: any) => {
          return h / 2 - y(d.valueToRepresent);
        });
      histogramBar
        .select(".violin__histogram--bar-text")
        .attr("x", histogramBarWidth / 2)
        .text((d: any) => {
          const v = d.valueToShowInLabel;
          if (v !== 0) {
            return v;
          } else {
            return "";
          }
        });
    } else {
      container.select(".violin__histogram").remove();
    }
    if (showKdePoints) {
      let kdePointsLayer = container.select(".violin__kde-points");
      if (kdePointsLayer.empty()) {
        kdePointsLayer = container.append("g").attr("class", "violin__kde-points");
      }
      const kdePointsS = kdePointsLayer.selectAll(".violin__kde-point").data(() => mainDim.kdeData);
      kdePointsS.exit().remove();
      const kdePoint = kdePointsS.enter().append("g").attr("class", "violin__kde-point");
      kdePoint
        .append("text")
        .attr("class", "violin__kde-point--text")
        .attr("text-anchor", "middle")
        .attr("y", "-0.5em");
      kdePoint.append("circle").attr("r", 2);
      kdePointsS.attr("transform", (d: any) => "translate(" + x(d[0]) + ", " + y(d[1]) + ")");
      kdePointsS.select(".violin__kde-point--text");
      if (showKdePointLabels) {
        kdePointsS.selectAll(".violin__kde-point--text").text((dd: any) => dd[1].toFixed(2));
      }
    } else {
      container.select(".violin__kde-points").remove();
    }
  };

  chart.width = function (_: any) {
    if (!arguments.length) {
      return width;
    } else {
      width = _;
      return chart;
    }
  };
  chart.height = function (_: any) {
    if (!arguments.length) {
      return height;
    } else {
      height = _;
      return chart;
    }
  };
  chart.margin = function (_: any) {
    if (!arguments.length) {
      return margin;
    } else {
      margin = _;
      return chart;
    }
  };
  chart.selectedDim = function (_: any) {
    if (!arguments.length) {
      return selectedDimKey;
    } else {
      selectedDimKey = _;
      return chart;
    }
  };
  chart.useOutline = function (_: any) {
    if (!arguments.length) {
      return useOutline;
    } else {
      useOutline = d3.functor(_);
      return chart;
    }
  };
  chart.xdomain = function (_: any) {
    if (!arguments.length) {
      return xdomain;
    } else {
      xdomain = _;
      return chart;
    }
  };
  chart.ydomain = function (_: any) {
    if (!arguments.length) {
      return ydomain;
    } else {
      ydomain = _;
      return chart;
    }
  };
  chart.valueFormat = function (_: any) {
    if (!arguments.length) {
      return valueFormat;
    } else {
      valueFormat = _;
      return chart;
    }
  };
  chart.axisLabels = function (_: any) {
    if (!arguments.length) {
      return axisLabels;
    } else {
      axisLabels = _;
      return chart;
    }
  };
  chart.showHistogram = function (_: any) {
    if (!arguments.length) {
      return showHistogram;
    } else {
      showHistogram = _;
      return chart;
    }
  };
  chart.showAxisTicks = function (_: any) {
    if (!arguments.length) {
      return showAxisTicks;
    } else {
      showAxisTicks = _;
      return chart;
    }
  };
  chart.showKdePoints = function (_: any) {
    if (!arguments.length) {
      return showKdePoints;
    } else {
      showKdePoints = _;
      return chart;
    }
  };
  chart.showKdePointLabels = function (_: any) {
    if (!arguments.length) {
      return showKdePointLabels;
    } else {
      showKdePointLabels = _;
      return chart;
    }
  };
  chart.showAvgValueText = function (_: any) {
    if (!arguments.length) {
      return showAvgValueText;
    } else {
      showAvgValueText = _;
      return chart;
    }
  };
  chart.histogramBins = function (_: any) {
    if (!arguments.length) {
      return histogramBins;
    } else {
      histogramBins = d3.functor(_);
      return chart;
    }
  };
  chart.kdeLineInterpolation = function (_: any) {
    if (!arguments.length) {
      return kdeLineInterpolation;
    } else {
      kdeLineInterpolation = _;
      return chart;
    }
  };
  chart.dimensionsOrdering = function (_: any) {
    if (!arguments.length) {
      return dimensionsOrdering;
    } else {
      dimensionsOrdering = _.reduce((m: any, dimKey: any, i: any) => {
        m[dimKey] = i;
        return m;
      }, {});
      return chart;
    }
  };
  d3.rebind(chart, dispatch, "on");
  return chart;
};
